import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiClock, FiPlay, FiCheck, FiAlertTriangle, FiHelpCircle } from 'react-icons/fi';

/**
 * The five-state universal status system for the staff (shop-floor) view.
 * StatusPill is the SINGLE SOURCE OF TRUTH — every other staff screen maps its
 * domain statuses (task/part/service/job/issue) onto one of these five buckets
 * via {@link mapStatus}.
 *
 *   waiting        ⚪ grey   — not started / queued
 *   in_progress    🔵 blue   — actively being worked on
 *   done           🟢 green  — finished / installed / completed
 *   problem        🔴 red    — rejected / failed / blocked
 *   needs_approval 🟠 amber  — pending quotation / customer approval
 */
export const STATUS_KEYS = ['waiting', 'in_progress', 'done', 'problem', 'needs_approval'];

// Visual + semantic config per state. Colors use static Tailwind classes so the
// JIT compiler keeps them; do not build these class strings dynamically.
const STATUS_CONFIG = {
  waiting: {
    key: 'waiting',
    Icon: FiClock,
    color: 'grey',
    className: 'bg-gray-100 text-gray-700 dark:bg-gray-500/15 dark:text-gray-300',
    dotClassName: 'bg-gray-400',
  },
  in_progress: {
    key: 'in_progress',
    Icon: FiPlay,
    color: 'blue',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300',
    dotClassName: 'bg-blue-500',
  },
  done: {
    key: 'done',
    Icon: FiCheck,
    color: 'green',
    className: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300',
    dotClassName: 'bg-green-500',
  },
  problem: {
    key: 'problem',
    Icon: FiAlertTriangle,
    color: 'red',
    className: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
    dotClassName: 'bg-red-500',
  },
  needs_approval: {
    key: 'needs_approval',
    Icon: FiHelpCircle,
    color: 'amber',
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
    dotClassName: 'bg-amber-500',
  },
};

// Maps every known domain status onto one of the five buckets. Anything not
// listed here resolves to 'waiting' (the safe default).
const DOMAIN_TO_STATUS = {
  // ---- waiting ----
  waiting: 'waiting',
  pending: 'waiting',
  received: 'waiting',
  queued: 'waiting',
  not_started: 'waiting',
  diagnosis: 'waiting',
  waiting_parts: 'waiting',
  // ---- in_progress ----
  in_progress: 'in_progress',
  active: 'in_progress',
  started: 'in_progress',
  quality_check: 'in_progress',
  // ---- done ----
  done: 'done',
  completed: 'done',
  // A skipped sub-task is terminal — it unblocks dependents and counts toward
  // the service rollup exactly like completed, so the shop floor reads it as
  // settled, not as still-waiting.
  skipped: 'done',
  installed: 'done',
  approved: 'done',
  ready: 'done',
  delivered: 'done',
  passed: 'done',
  // ---- problem ----
  problem: 'problem',
  rejected: 'problem',
  failed: 'problem',
  blocked: 'problem',
  cancelled: 'problem',
  // ---- needs_approval ----
  needs_approval: 'needs_approval',
  pending_approval: 'needs_approval',
  customer_approval: 'needs_approval',
  partially_approved: 'needs_approval',
};

/**
 * Map a raw domain status string to one of the five canonical states.
 * Unknown / empty / null inputs resolve to 'waiting'.
 *
 * @param {string} domainStatus
 * @returns {{key:string, color:string, Icon:Function, className:string, dotClassName:string}}
 */
export function mapStatus(domainStatus) {
  const normalized = String(domainStatus || '').trim().toLowerCase();
  const stateKey = DOMAIN_TO_STATUS[normalized] || 'waiting';
  return STATUS_CONFIG[stateKey];
}

/**
 * Visual pill for a status. Pass either a raw `status` (domain string, mapped
 * internally) — icon + color + a short localized word.
 */
const StatusPill = ({ status, className = '', showLabel = true, size = 'md' }) => {
  const { t } = useTranslation();
  const cfg = mapStatus(status);
  const { Icon } = cfg;

  const sizeClasses =
    size === 'sm'
      ? 'text-xs px-2 py-0.5 gap-1'
      : 'text-sm px-2.5 py-1 gap-1.5';
  const iconSize = size === 'sm' ? 12 : 14;

  return (
    <span
      data-testid="status-pill"
      data-status={cfg.key}
      className={`inline-flex items-center rounded-full font-semibold ${sizeClasses} ${cfg.className} ${className}`}
    >
      <Icon size={iconSize} className="shrink-0" />
      {showLabel && <span>{t(`staff.status.${cfg.key}`)}</span>}
    </span>
  );
};

export default StatusPill;
